import { AncillaryChargeType, ChargeTripItemType, ChargeType } from '../graphql/generated/graphql';
import { ancillaryLineLabel } from './ancillaryLabels';

/**
 * Structural rather than fragment-bound so both `DefaultTripFragment` and `DefaultCrmTripFragment`
 * satisfy it — the breakdown `type` is a plain String on the wire, so there is no enum coupling.
 */
type BreakdownLike = {
  type: string;
  amount: number;
  currency?: string | null;
  ancillaryType?: AncillaryChargeType | null;
  description?: string | null;
};

type ChargeRecordLike = {
  type: ChargeType;
  breakdown?: readonly BreakdownLike[] | null;
};

/** Every breakdown entry from captured charges. Refunds are excluded — they are reported separately. */
export function chargedBreakdown(records: readonly ChargeRecordLike[] | null | undefined): BreakdownLike[] {
  return (records ?? [])
    .filter((record) => record.type === ChargeType.Charge)
    .flatMap((record) => record.breakdown ?? []);
}

/**
 * Sum a breakdown type across every captured charge — never take the first match.
 *
 * A trip now routinely carries more than one charge record (the booking capture, plus any later
 * ancillary charge) and each brings its own booking fee and surcharge. Reading with `.find()` both
 * under-reported and was non-deterministic, because the `tripChargeRecords` association has no
 * ordering, so "first" was whatever Postgres happened to return.
 */
export function sumChargedBreakdown(
  records: readonly ChargeRecordLike[] | null | undefined,
  type: ChargeTripItemType,
): number {
  return chargedBreakdown(records)
    .filter((item) => item.type === type)
    .reduce((total, item) => total + item.amount, 0);
}

/** The ancillary lines of a trip, labelled for display on the price summary and tax invoice. */
export function ancillaryLines(
  records: readonly ChargeRecordLike[] | null | undefined,
): { label: string; amount: number }[] {
  return chargedBreakdown(records)
    .filter((item) => item.type === ChargeTripItemType.Ancillary)
    .map((item) => ({
      label: ancillaryLineLabel(item.ancillaryType, item.description),
      amount: item.amount,
    }));
}

type RefundRecordLike = {
  type: ChargeType;
  /** Money moved by the record, always positive */
  total: number;
};

/**
 * What the trip gave back across its refund records, so a payment summary can show the refund and net it off what was
 * charged instead of leaving a cancelled stay in the total.
 */
export function sumRefunded(records: readonly RefundRecordLike[] | null | undefined): number {
  return (records ?? [])
    .filter((record) => record.type === ChargeType.Refund)
    .reduce((total, record) => total + record.total, 0);
}
